"use client"
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../pagination';
import { useAuth } from "../../AuthContext";
import { encryptId } from "../../utils/crypto";
import { motion } from "framer-motion";

export default function ProjectsSection({ searchQuery, userId }) { 
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOwner, setIsOwner] = useState(false);
  const itemsPerPage = 12;
  const navigate = useNavigate();
  const { user } = useAuth();
  userId = userId || 0;

  useEffect(() => {
    fetchProjects();
  }, [userId]);

  useEffect(() => {
    if (user) {
      setIsOwner(user.id == userId);
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredProjects(
        projects.filter(project =>
          (project.project_title && project.project_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (project.professional_name && project.professional_name.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      );
    } else {
      setFilteredProjects(projects);
    }
  }, [searchQuery, projects]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`http://localhost:5000/professionals-projects?userId=${userId}`);
      const data = await response.json();
      setProjects(data);
      setFilteredProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleEditProject = (projectId) => {
    const projectRecord = projects.find((project) => project.professional_project_id === projectId);
    if (projectRecord) {
      navigate("/AddProject", { state: { projectRecord } });
    }
  };

  const handleRemoveProject = async (projectId) => {
    if (window.confirm("Are you sure you want to remove this project?")) {
      try {
        const response = await fetch(`http://localhost:5000/delete-project/${projectId}`, {
          method: "DELETE",
        });
        const result = await response.json();
        if (response.ok) {
          alert(result.message);
          setProjects(projects.filter((project) => project.professional_project_id !== projectId));
          setFilteredProjects(filteredProjects.filter((project) => project.professional_project_id !== projectId));
        } else {
          alert(`Failed to remove project: ${result.error}`);
        }
      } catch (error) {
        console.error("Error removing project:", error);
        alert("An error occurred while removing the project.");
      }
    }
  };

  const indexOfLastProject = currentPage * itemsPerPage;
  const indexOfFirstProject = indexOfLastProject - itemsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <section style={{ marginBottom: '64px' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <div style={{
          width: '4px',
          height: '32px',
          borderRadius: '2px',
          background: 'linear-gradient(180deg, var(--accent-violet), var(--accent-magenta))',
        }} />
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
        }}>Available Projects</h2>
        <div style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          background: 'rgba(124, 58, 237, 0.08)',
          border: '1px solid rgba(124, 58, 237, 0.15)',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#a78bfa',
        }}>
          {filteredProjects.length}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {currentProjects.map((project, index) => ( 
          <motion.div
            key={project.professional_project_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
          >
            <div style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-glass)',
              borderRadius: '20px',
              padding: '28px 24px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-6px)';
              e.currentTarget.style.borderColor = 'rgba(124, 58, 237, 0.3)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(124, 58, 237, 0.08)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'var(--border-glass)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              {/* Accent bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, var(--accent-violet), var(--accent-magenta), transparent)',
                opacity: 0.5,
              }} />

              <Link
                to={`/AvailProjectDetail/${encodeURIComponent(encryptId(project.professional_project_id))}`}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'var(--accent-violet)',
                    boxShadow: '0 0 8px rgba(124, 58, 237, 0.4)',
                  }} />
                  <h3 style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}>{project.project_title}</h3>
                </div>
                <p style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {project.professional_name}
                </p>
              </Link>

              {isOwner && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  marginTop: '16px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-subtle)',
                }}>
                  <button
                    onClick={() => handleEditProject(project.professional_project_id)}
                    title="Edit"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-glass)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'var(--accent-cyan)';
                      e.currentTarget.style.color = 'var(--accent-cyan)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--border-glass)';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleRemoveProject(project.professional_project_id)}
                    title="Remove"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-glass)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#f87171';
                      e.currentTarget.style.color = '#f87171';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--border-glass)';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                  >
                    ❌
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <Pagination
        itemsPerPage={itemsPerPage}
        totalItems={filteredProjects.length}
        paginate={paginate}
        currentPage={currentPage}
      />
    </section>
  );
}

